/**
 * oelParse.js — light client-side OEL utilities.
 *
 * The runtime evaluator lives server-side (Project-O-New `app/oel/`). Here
 * we only need: extract refs, validate roots/functions, find caret context.
 * Mirrors Project O's regex `@([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_]…|\[…\])*)`.
 */

import {
    OEL_FUNCTION_INDEX
} from './oelTokens';

const REF_REGEX = /@([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*|\[[^\]]+\])*)/g;
const SINGLE_QUOTE_REGEX = /'(?:[^'\\]|\\.)*'/g;
const DOUBLE_QUOTE_REGEX = /"(?:[^"\\]|\\.)*"/g;
const FUNC_CALL_REGEX = /\b([A-Za-z_][A-Za-z0-9_]*)\s*\(/g;
const BARE_WORD_REGEX = /\b([A-Za-z_][A-Za-z0-9_]*)\b/g;
const OEL_KEYWORDS = new Set(['and', 'or', 'not', 'true', 'false', 'null']);

/**
 * Strip out string literals (single- and double-quoted) so refs/identifiers
 * inside them are not analysed.
 */
function maskLiterals(src) {
    return src
        .replace(SINGLE_QUOTE_REGEX, (m) => ' '.repeat(m.length))
        .replace(DOUBLE_QUOTE_REGEX, (m) => ' '.repeat(m.length));
}

/**
 * Count top-level comma-separated arguments inside a function call body.
 * `body` is the text between the outermost parens. Commas inside nested
 * parens or string literals are not counted as separators.
 * Returns 0 for an empty call like `fn()`.
 */
function countArgs(body) {
    const masked = maskLiterals(body);
    if (!masked.trim()) return 0;
    let depth = 0;
    let count = 1;
    for (const ch of masked) {
        if (ch === '(') depth++;
        else if (ch === ')') depth--;
        else if (ch === ',' && depth === 0) count++;
    }
    return count;
}

/** Find all @refs in a value. Returns [{ root, path, full, start, end }]. */
export function extractRefs(value) {
    if (!value) return [];
    const masked = maskLiterals(String(value));
    const out = [];
    let m;
    REF_REGEX.lastIndex = 0;
    while ((m = REF_REGEX.exec(masked)) !== null) {
        const fullPath = m[1]; // e.g. "step_1.output.text"
        const root = fullPath.split(/[.[]/)[0];
        out.push({
            root,
            path: fullPath,
            full: '@' + fullPath,
            start: m.index,
            end: m.index + m[0].length
        });
    }
    return out;
}

/**
 * Validate a value against a build-time context. Returns
 *   { ok: bool, errors: [{ kind, message, ref?, fn? }] }.
 *
 * Build-time only: we don't try to detect runtime type errors. We catch:
 *   - unknown reference root (not @input, not a previous step, not a flow,
 *     not a definition, not a loop var)
 *   - unknown function name
 *   - unbalanced single-quote string literals
 *   - empty @ref ("@" alone)
 */
export function validate(value, ctx) {
    if (!value) return {
        ok: true,
        errors: []
    };
    const src = String(value);
    const errors = [];

    // ── unbalanced single quotes ──────────────────────────────────
    let quoteCount = 0;
    for (let i = 0; i < src.length; i++) {
        if (src[i] === '\\' && i + 1 < src.length) { i++; continue; }
        if (src[i] === "'") quoteCount++;
    }
    if (quoteCount % 2 === 1) {
        errors.push({
            kind: 'syntax',
            message: 'Unbalanced single quote in string literal.'
        });
    }

    // ── unbalanced parentheses ────────────────────────────────────
    const masked = maskLiterals(src);
    let parenDepth = 0;
    let parenBad = false;
    for (const ch of masked) {
        if (ch === '(') parenDepth++;
        else if (ch === ')') {
            parenDepth--;
            if (parenDepth < 0) { parenBad = true; break; }
        }
    }
    if (parenBad || parenDepth !== 0) {
        errors.push({
            kind: 'syntax',
            message: 'Unbalanced parentheses.'
        });
    }

    // ── unbalanced square brackets ────────────────────────────────
    let bracketDepth = 0;
    let bracketBad = false;
    for (const ch of masked) {
        if (ch === '[') bracketDepth++;
        else if (ch === ']') {
            bracketDepth--;
            if (bracketDepth < 0) { bracketBad = true; break; }
        }
    }
    if (bracketBad || bracketDepth !== 0) {
        errors.push({
            kind: 'syntax',
            message: 'Unbalanced square brackets.'
        });
    }

    // ── empty @ ───────────────────────────────────────────────────
    const emptyAt = /(^|[^A-Za-z0-9_])@($|[^A-Za-z_])/g;
    if (emptyAt.test(src)) {
        errors.push({
            kind: 'syntax',
            message: 'Empty @ — provide a reference root.'
        });
    }

    // ── unknown reference roots ───────────────────────────────────
    const refs = extractRefs(src);
    const knownRoots = new Set(['input']);
    if (ctx?.subflowAvailable) knownRoots.add('subflow');
    const prevSteps = ctx?.previousSteps || [];
    if (prevSteps.length) {
        prevSteps.forEach((s) => s?.id && knownRoots.add(s.id));
    } else {
        (ctx?.previousStepIds || []).forEach((id) => id && knownRoots.add(id));
    }
    (ctx?.definitions || []).forEach((d) => d?.id && knownRoots.add(d.id));
    if (!ctx?.inFlowScope) {
        (ctx?.flows || []).forEach((f) => f?.id && knownRoots.add(f.id));
    }
    const flowIdsInScope = ctx?.inFlowScope
        ? new Set((ctx?.flowCatalog || ctx?.flows || []).map((f) => f?.id).filter(Boolean))
        : null;
    const lvs = ctx?.loopVars || [];
    if (lvs.length) {
        lvs.forEach((lv) => lv?.name && knownRoots.add(lv.name));
    } else {
        (ctx?.loopVariables || []).forEach((n) => n && knownRoots.add(n));
    }

    for (const r of refs) {
        if (flowIdsInScope?.has(r.root)) {
            errors.push({
                kind: 'unknown_ref',
                ref: r.full,
                message: `Flow output must reference a step in this flow (e.g. @step_1.output.result), not @${r.root}.`
            });
            continue;
        }
        if (!knownRoots.has(r.root)) {
            errors.push({
                kind: 'unknown_ref',
                ref: r.full,
                message: `Unknown reference "@${r.root}". Pick one from the @ menu.`
            });
        }
    }

    // ── function name + argument count ────────────────────────────
    let fm;
    FUNC_CALL_REGEX.lastIndex = 0;
    while ((fm = FUNC_CALL_REGEX.exec(masked)) !== null) {
        const fname = fm[1];
        const at = fm.index;
        if (at > 0 && masked[at - 1] === '.') continue;
        if (OEL_KEYWORDS.has(fname)) continue;

        const fnDef = OEL_FUNCTION_INDEX[fname];
        if (!fnDef) {
            errors.push({
                kind: 'unknown_fn',
                fn: fname,
                message: `Unknown function "${fname}". Pick one from the Functions panel.`
            });
            continue;
        }

        // find the matching closing paren to extract the argument body
        const openPos = fm.index + fm[0].length - 1; // position of '('
        let depth = 1;
        let closePos = -1;
        for (let i = openPos + 1; i < masked.length; i++) {
            if (masked[i] === '(') depth++;
            else if (masked[i] === ')') {
                depth--;
                if (depth === 0) { closePos = i; break; }
            }
        }
        if (closePos !== -1 && fnDef.args) {
            const body = src.slice(openPos + 1, closePos);
            const argc = countArgs(body);
            const {
                min, max
            } = fnDef.args;
            if (argc < min || argc > max) {
                const expected = min === max ? `${min}` : `${min}–${max}`;
                errors.push({
                    kind: 'wrong_args',
                    fn: fname,
                    message: `${fname}() expects ${expected} argument${min === 1 && max === 1 ? '' : 's'}, got ${argc}.`
                });
            }
        }
    }

    // ── bare unknown identifiers ──────────────────────────────────
    const maskedFull = maskLiterals(src)
        .replace(REF_REGEX, (m) => ' '.repeat(m.length));
    BARE_WORD_REGEX.lastIndex = 0;
    let bm;
    while ((bm = BARE_WORD_REGEX.exec(maskedFull)) !== null) {
        const word = bm[1];
        if (OEL_KEYWORDS.has(word)) continue;
        if (bm.index > 0 && maskedFull[bm.index - 1] === '.') continue;
        const after = maskedFull.slice(bm.index + word.length).trimStart();
        if (after.startsWith('(')) continue;
        errors.push({
            kind: 'unknown_identifier',
            message: `Unknown identifier "${word}" — use @ref, a function, or a quoted literal.`
        });
    }

    return {
        ok: errors.length === 0,
        errors
    };
}

/**
 * Inspect the caret position to decide what completion menu (if any)
 * should open. Returns one of:
 *   { kind: 'none' }
 *   { kind: 'ref-root', anchor: number }            — user just typed `@`
 *   { kind: 'ref-path', anchor: number, root: str } — typing after `@root.`
 *   { kind: 'fn-args', fn: str }                    — typing inside fn(…)
 */
export function caretContext(value, caret) {
    if (!value) return {
        kind: 'none'
    };
    const before = value.slice(0, caret);

    // after @ but before next break char → ref typing
    const atMatch = /@([A-Za-z_][A-Za-z0-9_]*)?(\.[A-Za-z_][A-Za-z0-9_]*)*\.?$/.exec(before);
    if (atMatch && atMatch.index !== undefined) {
        // Make sure not inside a single-quoted string
        const masked = maskLiterals(before);
        if (masked[atMatch.index] === '@') {
            const root = atMatch[1] || '';
            // If user has just typed "@" with no root → root menu
            if (!root) return {
                kind: 'ref-root',
                anchor: atMatch.index
            };
            // If user typed "@root" or "@root." → path menu (still useful)
            return {
                kind: 'ref-path',
                anchor: atMatch.index,
                root
            };
        }
    }

    return {
        kind: 'none'
    };
}

/** Insert a snippet into `value` at caret, returns { next, caret }. */
export function insertAt(value, caret, snippet) {
    const v = value || '';
    const next = v.slice(0, caret) + snippet + v.slice(caret);
    return {
        next,
        caret: caret + snippet.length
    };
}

/**
 * Replace from `from` to `to` in value with `snippet`. Used to replace a
 * partially-typed @ref when the user picks from the menu.
 */
export function replaceRange(value, from, to, snippet) {
    const v = value || '';
    const next = v.slice(0, from) + snippet + v.slice(to);
    return {
        next,
        caret: from + snippet.length
    };
}
